import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Send, CheckCircle2, XCircle, Clock, Loader2, Filter, User, BookOpen, AlertCircle
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { AdminLayout } from "../../components/layout/AdminLayout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const STATUS_CONFIG = {
  pending: { label: "Pending", icon: Clock, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  approved: { label: "Approved", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  rejected: { label: "Denied", icon: XCircle, color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
};

const AccessRequestsPage = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const variant = user?.role === "manager" ? "manager" : user?.role === "tenant_admin" ? "tenant" : "admin";

  const fetchRequests = useCallback(async () => {
    try {
      const params = statusFilter !== "all" ? `?status_filter=${statusFilter}` : "";
      const res = await api.get(`/course-access-requests${params}`);
      setRequests(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleAction = async (requestId, action) => {
    setActionLoading(`${requestId}-${action}`);
    try {
      await api.put(`/course-access-requests/${requestId}`, { action });
      toast.success(`Request ${action}`);
      setRequests(prev => prev.map(r =>
        r.id === requestId ? { ...r, status: action, reviewed_at: new Date().toISOString() } : r
      ));
      setConfirmDialog(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action} request`);
    } finally {
      setActionLoading(null);
    }
  };

  const pending = requests.filter(r => r.status === "pending");
  const resolved = requests.filter(r => r.status !== "pending");
  const filtered = statusFilter === "all" ? requests : requests.filter(r => r.status === statusFilter);

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <AdminLayout title="Access Requests" variant={variant}>
      <div className="space-y-6" data-testid="access-requests-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>
              Access Requests
            </h1>
            <p className="text-muted-foreground text-sm">
              Review and manage course access requests
              {pending.length > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-amber-600 font-semibold">
                  <AlertCircle className="w-3.5 h-3.5" /> {pending.length} pending
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "Pending", count: requests.filter(r => r.status === "pending").length, cfg: STATUS_CONFIG.pending },
            { label: "Approved", count: requests.filter(r => r.status === "approved").length, cfg: STATUS_CONFIG.approved },
            { label: "Denied", count: requests.filter(r => r.status === "rejected").length, cfg: STATUS_CONFIG.rejected },
          ].map(s => (
            <Card key={s.label} className={`${s.cfg.border} border`} data-testid={`stat-${s.label.toLowerCase()}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.cfg.bg}`}>
                  <s.cfg.icon className={`w-5 h-5 ${s.cfg.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{s.count}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] h-9" data-testid="status-filter">
              <Filter className="w-3 h-3 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Requests</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Denied</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Request List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Send className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-muted-foreground">No access requests found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((req, i) => {
              const cfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
              const StatusIcon = cfg.icon;
              return (
                <motion.div key={req.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                  <Card
                    className={`transition-colors ${req.status === "pending" ? "border-amber-200 bg-amber-50/20" : ""}`}
                    data-testid={`request-${req.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${cfg.bg}`}>
                            <User className={`w-5 h-5 ${cfg.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-sm">{req.user_name || "Unknown User"}</h4>
                              <Badge variant="outline" className={`text-[10px] ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                                <StatusIcon className="w-3 h-3 mr-0.5" />
                                {cfg.label}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">{req.user_email}</p>
                            <div className="flex items-center gap-1.5 mt-1.5">
                              <BookOpen className="w-3.5 h-3.5 text-primary" />
                              <span className="text-sm font-medium">{req.course_title}</span>
                            </div>
                            {req.reason && (
                              <p className="text-xs text-muted-foreground mt-1.5 bg-slate-50 rounded-md p-2 italic">
                                "{req.reason}"
                              </p>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                              <span>Requested {timeAgo(req.created_at)}</span>
                              {req.reviewed_at && (
                                <span>Reviewed {timeAgo(req.reviewed_at)}{req.reviewed_by_name ? ` by ${req.reviewed_by_name}` : ""}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Action buttons for pending requests */}
                        {req.status === "pending" && (
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                              onClick={() => setConfirmDialog({ req, action: "approved" })}
                              disabled={!!actionLoading}
                              data-testid={`approve-btn-${req.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-200 text-red-700 hover:bg-red-50"
                              onClick={() => setConfirmDialog({ req, action: "rejected" })}
                              disabled={!!actionLoading}
                              data-testid={`deny-btn-${req.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Deny
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Confirmation Dialog */}
        <Dialog open={!!confirmDialog} onOpenChange={(v) => { if (!v) setConfirmDialog(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {confirmDialog?.action === "approved" ? (
                  <><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Approve Request</>
                ) : (
                  <><XCircle className="w-5 h-5 text-red-600" /> Deny Request</>
                )}
              </DialogTitle>
              <DialogDescription>
                {confirmDialog?.action === "approved"
                  ? `This will enroll ${confirmDialog?.req?.user_name} in "${confirmDialog?.req?.course_title}" immediately.`
                  : `This will deny ${confirmDialog?.req?.user_name}'s request for "${confirmDialog?.req?.course_title}".`
                }
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDialog(null)}>Cancel</Button>
              <Button
                variant={confirmDialog?.action === "approved" ? "default" : "destructive"}
                onClick={() => handleAction(confirmDialog.req.id, confirmDialog.action)}
                disabled={!!actionLoading}
                data-testid="confirm-action-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {confirmDialog?.action === "approved" ? "Approve" : "Deny"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AccessRequestsPage;
